import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronRight, Linkedin, Twitter, Mail } from "lucide-react";
import { Card } from "../components/ui/card";
import { findPost, relatedPosts } from "../data/blogPosts";
import NotFound from "./NotFound";

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const RelatedCover = ({ post }) => (
    <div className="aspect-[16/9] rounded-md bg-gradient-to-br from-[#0A2540] to-[#127A75]" aria-hidden="true" />
);

const Body = ({ blocks }) => (
    <div className="space-y-5">
        {blocks.map((b, i) => {
            if (b.type === "h2") {
                return (
                    <h2 key={i} className="mt-10 text-2xl sm:text-3xl font-bold text-brand-text tracking-tight">
                        {b.text}
                    </h2>
                );
            }
            if (b.type === "list") {
                return (
                    <ul key={i} className="space-y-2 list-disc list-outside pl-5 text-base text-brand-body leading-relaxed">
                        {b.items.map((item, j) => <li key={j}>{item}</li>)}
                    </ul>
                );
            }
            return (
                <p key={i} className="text-base text-brand-body leading-relaxed">
                    {b.text}
                </p>
            );
        })}
    </div>
);

const Breadcrumb = ({ post }) => (
    <nav aria-label="Breadcrumb" className="text-sm text-brand-muted">
        <ol className="flex items-center gap-1.5 flex-wrap">
            <li><Link to="/" className="hover:text-brand-teal">Home</Link></li>
            <ChevronRight size={14} aria-hidden="true" />
            <li><Link to="/resources" className="hover:text-brand-teal">Resources</Link></li>
            <ChevronRight size={14} aria-hidden="true" />
            <li className="text-brand-text font-medium line-clamp-1" aria-current="page">{post.title}</li>
        </ol>
    </nav>
);

const Cover = ({ post }) => (
    <div className="relative w-full aspect-[16/7] sm:aspect-[16/6] overflow-hidden rounded-lg bg-gradient-to-br from-[#0A2540] via-[#0F3552] to-[#127A75]" aria-hidden="true">
        <svg viewBox="0 0 800 300" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-40">
            <circle cx="120" cy="80" r="60" fill="#1A8B82" />
            <circle cx="660" cy="230" r="90" fill="#FFFFFF" opacity="0.2" />
            <rect x="380" y="100" width="120" height="120" rx="16" fill="#FFFFFF" opacity="0.15" />
        </svg>
    </div>
);

const ShareRow = ({ title }) => {
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const items = [
        { icon: Linkedin, label: "Share on LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
        { icon: Twitter, label: "Share on Twitter", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}` },
        { icon: Mail, label: "Email this article", href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareUrl)}` },
    ];
    return (
        <ul className="flex items-center gap-2" aria-label="Share article">
            {items.map((it) => {
                const Icon = it.icon;
                return (
                    <li key={it.label}>
                        <a href={it.href} target="_blank" rel="noopener noreferrer"
                           aria-label={it.label}
                           className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-brand-border text-brand-navy hover:bg-brand-soft transition-colors">
                            <Icon size={16} aria-hidden="true" />
                        </a>
                    </li>
                );
            })}
        </ul>
    );
};

export default function BlogPost() {
    const { slug } = useParams();
    const post = findPost(slug);

    useEffect(() => {
        if (post) document.title = `${post.title} — AccessNova`;
    }, [post]);

    if (!post || !post.published) return <NotFound />;

    const related = relatedPosts(post.slug);

    return (
        <article className="bg-white">
            <header className="border-b border-brand-border">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 pb-10">
                    <Breadcrumb post={post} />
                    <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-brand-teal">{post.category}</p>
                    <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-text tracking-tight">
                        {post.title}
                    </h1>
                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-muted">
                        <span className="flex items-center gap-1.5"><Calendar size={14} aria-hidden="true" />{formatDate(post.date)}</span>
                        <span className="flex items-center gap-1.5"><Clock size={14} aria-hidden="true" />{post.readTime}</span>
                        <span className="flex items-center gap-1.5">By <span className="text-brand-text font-medium">{post.author?.name}</span></span>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
                <Cover post={post} />
                <p className="mt-8 text-lg text-brand-body leading-relaxed font-medium">{post.excerpt}</p>
                <div className="mt-10">
                    <Body blocks={post.body || []} />
                </div>

                <div className="mt-14 pt-8 border-t border-brand-border flex flex-wrap items-center justify-between gap-5">
                    <div>
                        <p className="text-sm font-semibold text-brand-text">{post.author?.name}</p>
                        <p className="text-xs text-brand-muted">{post.author?.role}</p>
                    </div>
                    <ShareRow title={post.title} />
                </div>
            </div>

            {/* CTA */}
            <section aria-labelledby="post-cta" className="bg-brand-soft border-y border-brand-border py-16">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
                    <h2 id="post-cta" className="text-2xl sm:text-3xl font-bold text-brand-text">
                        See where your site stands. Free.
                    </h2>
                    <p className="mt-3 text-base text-brand-body">
                        A 1-page accessibility snapshot in your inbox within 48 hours. No obligation.
                    </p>
                    <Link to="/free-audit"
                          className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target"
                          data-testid="post-cta-audit">
                        Get a Free Mini Audit <ArrowRight size={18} aria-hidden="true" />
                    </Link>
                </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
                <section aria-labelledby="related" className="py-16">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <h2 id="related" className="text-2xl font-bold text-brand-text">More reading</h2>
                        <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map((p) => (
                                <li key={p.slug}>
                                    <Link to={`/resources/${p.slug}`}
                                          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:ring-offset-2 rounded-lg">
                                        <Card className="h-full p-5 border-brand-border hover:shadow-cardHover transition-shadow">
                                            <RelatedCover post={p} />
                                            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-brand-teal">{p.category}</p>
                                            <h3 className="mt-2 text-base font-bold text-brand-text leading-tight">{p.title}</h3>
                                        </Card>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </article>
    );
}
